/**
 * GlobalAudioHook - Simple audio playback for play buttons throughout the app
 *
 * Features:
 * - Play sound on button click
 * - Stop previous audio when new one starts
 * - Works globally without needing specific form context
 */
export const GlobalAudioHook = {
  mounted() {
    this.currentAudio = null;
    this.setupEventListeners();

    // Listen for LiveView sound events
    this.handleEvent("play_sound", (payload) => {
      if (payload.file) {
        const volume = payload.volume
          ? this.mapVolumeToAudio(payload.volume)
          : 1.0;
        const source = payload.base
          ? { dataset: { soundBase: payload.base } }
          : null;
        this.playSound(payload.file, volume, source);
      }
    });
  },

  destroyed() {
    this.stopCurrentAudio();

    if (this.handleDocumentClick) {
      document.removeEventListener("click", this.handleDocumentClick);
      this.handleDocumentClick = null;
    }
  },

  setupEventListeners() {
    // Listen for play button clicks anywhere in the app
    this.handleDocumentClick = (event) => {
      if (
        event.target.matches("button[data-play-audio]") ||
        event.target.closest("button[data-play-audio]")
      ) {
        event.preventDefault();
        this.handlePlayButton(event);
      }
    };

    document.addEventListener("click", this.handleDocumentClick);
  },

  handlePlayButton(event) {
    const button =
      event.target.closest("button[data-play-audio]") || event.target;
    const soundFile = button.dataset.playAudio;
    const volumePercent = button.dataset.volume
      ? parseInt(button.dataset.volume, 10)
      : 100;
    const audioVolume = this.mapVolumeToAudio(volumePercent);

    if (soundFile) {
      this.playSound(soundFile, audioVolume, button);
    }
  },

  playSound(soundFile, volume = 1.0, sourceEl = null) {
    this.stopCurrentAudio();

    try {
      const audioPath = this.resolveAudioPath(soundFile, sourceEl);

      if (!audioPath) {
        return;
      }

      const audio = new Audio(audioPath);
      this.currentAudio = audio;

      audio.volume = volume;

      // Error handling
      audio.addEventListener("error", (e) => {
        console.error("Global audio playback error:", e);
        console.error("Failed to load audio from:", audioPath);
      });

      // Clean up when audio ends
      audio.addEventListener("ended", () => {
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
      });

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.error("Global audio playback error:", error);
        });
      }
    } catch (error) {
      console.error("Failed to create global audio:", error);
    }
  },

  resolveAudioPath(soundFile, sourceEl) {
    if (!soundFile) return null;

    if (soundFile.startsWith("http://") || soundFile.startsWith("https://")) {
      return soundFile;
    }

    if (soundFile.startsWith("/")) {
      return soundFile;
    }

    const base =
      (sourceEl && sourceEl.dataset.soundBase) ||
      (this.el && this.el.dataset && this.el.dataset.soundBase) ||
      null;

    if (base) {
      const encoded = soundFile
        .split("/")
        .map((part) => encodeURIComponent(part))
        .join("/");
      return `${base}/${encoded}`;
    }

    return `/audio/${soundFile}`;
  },

  stopCurrentAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  },

  // Map volume percentage (50-300) to audio volume (0.1-1.0)
  // 300% -> 1.0, 275% -> 0.9, 250% -> 0.8, ..., 50% -> 0.1, 25% -> 0.1 (minimum)
  mapVolumeToAudio(volumePercent) {
    // Clamp to valid range
    const clampedPercent = Math.max(25, Math.min(300, volumePercent));

    // Calculate step (0-11, where 0 = 300%, 11 = 25%)
    const step = Math.floor((300 - clampedPercent) / 25);

    // Map to audio volume (1.0 down to 0.1)
    const audioVolume = Math.max(0.1, 1.0 - step * 0.1);

    return audioVolume;
  },
};
